import { useState, useEffect, useCallback } from "react";
import { MatchTimelineService, MatchStatus } from "../services/matchTimelineService";

interface TimelineEventOptions {
  minuteOverride?: number;
  videoTimeSeconds?: number | null;
  videoId?: string | null;
}

export interface UseMatchTimelineReturn {
  matchStatus: MatchStatus;
  isLoading: boolean;
  isTimerRunning: boolean;
  startMatch: (options?: TimelineEventOptions) => Promise<void>;
  endFirstHalf: (options?: TimelineEventOptions) => Promise<void>;
  startSecondHalf: (minute?: number) => Promise<void>;
  setFullTime: (options?: TimelineEventOptions) => Promise<void>;
  resetMatch: () => Promise<void>;
  toggleTimer: () => Promise<void>;
  refreshStatus: () => Promise<void>;
  // Extra-time controls
  startExtraTimeFirstHalf: (minute?: number) => Promise<void>;
  endExtraTimeFirstHalf: () => Promise<void>;
  startExtraTimeSecondHalf: (minute?: number) => Promise<void>;
  startPenaltyShootout: (minute?: number) => Promise<void>;
  // Editing controls
  enableEditingMode: () => Promise<void>;
  disableEditingMode: () => Promise<void>;
}

export const useMatchTimeline = (matchId: string): UseMatchTimelineReturn => {
  const [matchStatus, setMatchStatus] = useState<MatchStatus>({
    status: "not_started",
    current_minute: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  const refreshStatus = useCallback(async () => {
    if (!matchId) return;
    
    setIsLoading(true);
    try {
      const status = await MatchTimelineService.getMatchStatus(matchId);
      setMatchStatus(status);
    } catch (error) {
      console.error("Error fetching match status:", error);
    } finally {
      setIsLoading(false);
    }
  }, [matchId]);

  const startMatch = useCallback(async (options?: TimelineEventOptions) => {
    if (!matchId) return;
    
    // Optimistic update: Update UI immediately
    const previousStatus = matchStatus;
    const minute = options?.minuteOverride ?? 0;
    setMatchStatus(prev => ({
      ...prev,
      status: 'live',
      current_minute: minute || 1,
      kick_off_minute: minute
    }));
    
    try {
      await MatchTimelineService.recordMatchEvent(
        matchId,
        "kick_off",
        minute,
        undefined,
        undefined,
        options?.videoTimeSeconds ?? undefined,
        options?.videoId ?? undefined
      );
      // Sync with database (state already updated optimistically)
      await refreshStatus();
    } catch (error) {
      // Revert optimistic update on error
      setMatchStatus(previousStatus);
      console.error("Error starting match:", error);
      throw error;
    }
  }, [matchId, matchStatus, refreshStatus]);

  const endFirstHalf = useCallback(async (options?: TimelineEventOptions) => {
    if (!matchId) return;
    
    // Optimistic update: Update UI immediately
    const previousStatus = matchStatus;
    const minute = options?.minuteOverride ?? matchStatus.current_minute;
    setMatchStatus(prev => ({
      ...prev,
      status: 'half_time',
      current_minute: minute,
      end_1st_half_minute: minute
    }));
    
    try {
      await MatchTimelineService.recordMatchEvent(
        matchId,
        "end_1st_half",
        minute,
        undefined,
        undefined,
        options?.videoTimeSeconds ?? undefined,
        options?.videoId ?? undefined
      );
      // Sync with database (state already updated optimistically)
      await refreshStatus();
    } catch (error) {
      // Revert optimistic update on error
      setMatchStatus(previousStatus);
      console.error("Error ending first half:", error);
      throw error;
    }
  }, [matchId, matchStatus, refreshStatus]);

  const startSecondHalf = useCallback(async (minute: number = 45) => {
    if (!matchId) return;
    
    // Optimistic update: Update UI immediately
    const previousStatus = matchStatus;
    setMatchStatus(prev => ({
      ...prev,
      status: 'live',
      current_minute: minute,
      start_2nd_half_minute: minute
    }));
    
    try {
      await MatchTimelineService.recordMatchEvent(matchId, "start_2nd_half", minute);
      // Sync with database (state already updated optimistically)
      await refreshStatus();
    } catch (error) {
      // Revert optimistic update on error
      setMatchStatus(previousStatus);
      console.error("Error starting second half:", error);
      throw error;
    }
  }, [matchId, matchStatus, refreshStatus]);

  const setFullTime = useCallback(async (options?: TimelineEventOptions) => {
    if (!matchId) return;
    
    // Optimistic update: Update UI immediately
    const previousStatus = matchStatus;
    const minute = options?.minuteOverride ?? matchStatus.current_minute;
    setMatchStatus(prev => ({
      ...prev,
      status: 'full_time',
      current_minute: minute,
      full_time_minute: minute
    }));
    
    try {
      await MatchTimelineService.recordMatchEvent(
        matchId,
        "full_time",
        minute,
        undefined,
        undefined,
        options?.videoTimeSeconds ?? undefined,
        options?.videoId ?? undefined
      );
      // Sync with database (state already updated optimistically)
      await refreshStatus();
    } catch (error) {
      // Revert optimistic update on error
      setMatchStatus(previousStatus);
      console.error("Error setting full time:", error);
      throw error;
    }
  }, [matchId, matchStatus, refreshStatus]);

  const resetMatch = useCallback(async () => {
    if (!matchId) return;
    
    try {
      await MatchTimelineService.resetMatch(matchId);
      await refreshStatus();
    } catch (error) {
      console.error("Error resetting match:", error);
      throw error;
    }
  }, [matchId, refreshStatus]);

  const toggleTimer = useCallback(async () => {
    setIsTimerRunning(prev => !prev);
    // TODO: Implement actual timer functionality
    // This is a placeholder for the timer bug that needs to be fixed later
  }, []);

  // Extra-time controls
  const startExtraTimeFirstHalf = useCallback(async (minute: number = 90) => {
    if (!matchId) return;
    
    // Optimistic update: Update UI immediately
    const previousStatus = matchStatus;
    setMatchStatus(prev => ({
      ...prev,
      status: 'extra_time_1st_half',
      current_minute: minute,
      start_extra_time_1st_half_minute: minute
    }));
    
    try {
      await MatchTimelineService.recordMatchEvent(matchId, "start_extra_time_1st_half", minute);
      // Sync with database (state already updated optimistically)
      await refreshStatus();
    } catch (error) {
      // Revert optimistic update on error
      setMatchStatus(previousStatus);
      console.error("Error starting extra time first half:", error);
      throw error;
    }
  }, [matchId, matchStatus, refreshStatus]);

  const endExtraTimeFirstHalf = useCallback(async () => {
    if (!matchId) return;
    
    // Optimistic update: Update UI immediately
    const previousStatus = matchStatus;
    const minute = matchStatus.current_minute;
    setMatchStatus(prev => ({
      ...prev,
      status: 'extra_time_half_time',
      current_minute: minute,
      end_extra_time_1st_half_minute: minute
    }));
    
    try {
      await MatchTimelineService.recordMatchEvent(matchId, "end_extra_time_1st_half", minute);
      // Sync with database (state already updated optimistically)
      await refreshStatus();
    } catch (error) {
      // Revert optimistic update on error
      setMatchStatus(previousStatus);
      console.error("Error ending extra time first half:", error);
      throw error;
    }
  }, [matchId, matchStatus, refreshStatus]);

  const startExtraTimeSecondHalf = useCallback(async (minute: number = 105) => {
    if (!matchId) return;
    
    // Optimistic update: Update UI immediately
    const previousStatus = matchStatus;
    setMatchStatus(prev => ({
      ...prev,
      status: 'extra_time_2nd_half',
      current_minute: minute,
      start_extra_time_2nd_half_minute: minute
    }));
    
    try {
      await MatchTimelineService.recordMatchEvent(matchId, "start_extra_time_2nd_half", minute);
      // Sync with database (state already updated optimistically)
      await refreshStatus();
    } catch (error) {
      // Revert optimistic update on error
      setMatchStatus(previousStatus);
      console.error("Error starting extra time second half:", error);
      throw error;
    }
  }, [matchId, matchStatus, refreshStatus]);

  const startPenaltyShootout = useCallback(async (minute: number = 120) => {
    if (!matchId) return;
    
    // Optimistic update: Update UI immediately
    const previousStatus = matchStatus;
    setMatchStatus(prev => ({
      ...prev,
      status: 'penalty_shootout',
      current_minute: minute,
      penalty_shootout_minute: minute
    }));
    
    try {
      await MatchTimelineService.recordMatchEvent(matchId, "penalty_shootout", minute);
      // Sync with database (state already updated optimistically)
      await refreshStatus();
    } catch (error) {
      // Revert optimistic update on error
      setMatchStatus(previousStatus);
      console.error("Error starting penalty shootout:", error);
      throw error;
    }
  }, [matchId, matchStatus, refreshStatus]);

  // Editing controls
  const enableEditingMode = useCallback(async () => {
    if (!matchId) return;
    
    try {
      await MatchTimelineService.enableEditingMode(matchId);
      await refreshStatus();
    } catch (error) {
      console.error("Error enabling editing mode:", error);
      throw error;
    }
  }, [matchId, refreshStatus]);

  const disableEditingMode = useCallback(async () => {
    if (!matchId) return;
    
    try {
      await MatchTimelineService.disableEditingMode(matchId);
      await refreshStatus();
    } catch (error) {
      console.error("Error disabling editing mode:", error);
      throw error;
    }
  }, [matchId, refreshStatus]);

  // Load initial status
  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  return {
    matchStatus,
    isLoading,
    isTimerRunning,
    startMatch,
    endFirstHalf,
    startSecondHalf,
    setFullTime,
    resetMatch,
    toggleTimer,
    refreshStatus,
    // Extra-time controls
    startExtraTimeFirstHalf,
    endExtraTimeFirstHalf,
    startExtraTimeSecondHalf,
    startPenaltyShootout,
    // Editing controls
    enableEditingMode,
    disableEditingMode
  };
};
